import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, CreditCard, ShieldCheck } from "lucide-react";
import { createPortalSession } from "@/services/payments";
import { useEndUser } from "@/contexts/EndUserContext";

export type BlockedSubscriptionState = "expired" | "canceled" | "past_due" | "none";

interface SubscriptionGateOverlayProps {
  subscriptionState: BlockedSubscriptionState;
}

const STATE_CONFIG: Record<
  BlockedSubscriptionState,
  {
    icon: typeof AlertCircle;
    title: string;
    description: string;
    actionLabel: string;
    action: "subscribe" | "portal";
  }
> = {
  expired: {
    icon: ShieldCheck,
    title: "Your subscription has ended",
    description:
      "Your data is safe and waiting for you. Resubscribe to pick up right where you left off.",
    actionLabel: "Resubscribe",
    action: "subscribe",
  },
  canceled: {
    icon: ShieldCheck,
    title: "Your subscription has ended",
    description:
      "Your data is safe and waiting for you. Resubscribe to pick up right where you left off.",
    actionLabel: "Resubscribe",
    action: "subscribe",
  },
  past_due: {
    icon: CreditCard,
    title: "Payment failed",
    description:
      "We were unable to process your last payment. Please update your payment method to continue.",
    actionLabel: "Update payment method",
    action: "portal",
  },
  none: {
    icon: AlertCircle,
    title: "Subscription required",
    description: "Subscribe to access your recruiting assistant.",
    actionLabel: "Subscribe",
    action: "subscribe",
  },
};

/**
 * Full-page overlay that blocks interaction when the user's subscription
 * is in a non-active state (expired, canceled, past_due, none).
 */
export default function SubscriptionGateOverlay({
  subscriptionState,
}: SubscriptionGateOverlayProps) {
  const navigate = useNavigate();
  const { handleLogout } = useEndUser();
  const [loading, setLoading] = useState(false);

  const config = STATE_CONFIG[subscriptionState] ?? STATE_CONFIG.none;
  const Icon = config.icon;

  const handleAction = async () => {
    if (config.action === "subscribe") {
      navigate("/subscribe");
      return;
    }
    // Portal flow
    try {
      setLoading(true);
      const { url } = await createPortalSession(window.location.href);
      window.location.href = url;
    } catch {
      navigate("/subscribe");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <Card className="mx-4 w-full max-w-md shadow-lg">
        <CardContent className="flex flex-col items-center gap-4 pt-8 pb-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            <Icon className="h-7 w-7 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold tracking-tight">{config.title}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
            {config.description}
          </p>
          <Button onClick={handleAction} disabled={loading} className="mt-2 w-full max-w-xs">
            {loading ? "Loading..." : config.actionLabel}
          </Button>
          <button
            type="button"
            onClick={handleLogout}
            className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
          >
            Log out
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
